import React from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  StackActions,
  useNavigation,
} from '@react-navigation/native';

import { IconTouchable } from '../common/IconTouchable';
import { BaseText } from '../common/BaseText';
import { screens } from '@navigation';

interface IContainerStyleProps {
  topInset: number;
}

const Container = styled(View)((props: IContainerStyleProps) => ({
  bg: '$background0',
  pt: props.topInset,
  height: 64 + props.topInset,
  width: '100%',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexDirection: 'row',
  px: '$6',
}));

const LeftContainer = styled(View)({
  alignItems: 'flex-start',
  flex: 1,
});

const RightContainer = styled(View)({
  alignItems: 'flex-end',
  flex: 1,
});

const IconLeft = styled(IconTouchable)({
  p: '$1',
});

const Title = styled(BaseText)({
  color: '$heading900',
  fontWeight: 'semi',
  variant: 'text.large',
  textAlign: 'center',
  flex: 4,
});

interface ILeftComponentProps {
  goBack(): void;
}

interface IBaseMobileHeaderProps {
  LeftComponent?: React.ComponentType<ILeftComponentProps>;
  RightComponent?: React.ComponentType<any>;
  title?: string;
}

export function BaseMobileHeader({
  LeftComponent,
  RightComponent,
  title,
}: IBaseMobileHeaderProps) {
  const insets = useSafeAreaInsets();
  const nav = useNavigation();

  function goBack() {
    if (nav.canGoBack()) {
      nav.goBack();
    } else {
      // The app was opened via deep linking on a specific screen, this screen will be first
      // in the navigator and as a result, navigation back will be not possible. This can
      // cause issues the user cannot leave the "tutorial" screen for example and cannot
      // reach other content in the app. By using replace method we resolve the issue
      nav.dispatch(StackActions.replace(screens.TabNavigator));
    }
  }

  function headerLeft() {
    if (LeftComponent) {
      return <LeftComponent goBack={goBack} />;
    }

    return (
      <IconLeft name="small-arrow-left-light" onPress={goBack} />
    );
  }

  function headerRight() {
    if (RightComponent) {
      return <RightComponent />;
    }

    return null;
  }

  return (
    <Container topInset={insets.top}>
      <LeftContainer>{headerLeft()}</LeftContainer>
      {!!title && <Title numberOfLines={1}>{title}</Title>}
      <RightContainer>{headerRight()}</RightContainer>
    </Container>
  );
}
